import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent } from '@devkit/ui';
import { CheckCircle, XCircle } from 'lucide-react';
import { trackToolExecute, trackClear } from '@devkit/analytics';

const defaultJson = `{
  "name": "DevKit",
  "version": "1.0.0",
  "features": ["fast", "private", "free"]
}`;

export default function JsonValidatorPage() {
  const [input, setInput] = useState(defaultJson);
  const [result, setResult] = useState<{ valid: boolean; error?: string; position?: string } | null>(null);

  const validate = useCallback(() => {
    if (!input.trim()) {
      setResult(null);
      return;
    }
    trackToolExecute('json-validator', 'validate');

    try {
      JSON.parse(input);
      setResult({ valid: true });
    } catch (e) {
      const error = e as SyntaxError;
      let position = '';

      // Try to extract position from error message
      const posMatch = error.message.match(/position\s+(\d+)/i);
      if (posMatch) {
        const pos = parseInt(posMatch[1]);
        const lines = input.substring(0, pos).split('\n');
        const line = lines.length;
        const col = lines[lines.length - 1].length + 1;
        position = `Line ${line}, Column ${col}`;
      }

      setResult({
        valid: false,
        error: error.message,
        position,
      });
    }
  }, [input]);

  const clearAll = useCallback(() => {
    setInput('');
    setResult(null);
    trackClear('json-validator');
  }, []);

  return (
    <ToolLayout slug="json-validator">
      <Card>
        <CardContent className="p-6">
          {/* Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Input JSON
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your JSON here to validate..."
              className="w-full h-64 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <Button onClick={validate} className="gap-2">
              <CheckCircle className="w-4 h-4" />
              Validate
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {/* Result */}
          {result && (
            <div
              className={`p-4 rounded-lg border ${
                result.valid
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : 'bg-red-500/10 border-red-500/30'
              }`}
            >
              <div className="flex items-start gap-3">
                {result.valid ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${result.valid ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result.valid ? 'Valid JSON' : 'Invalid JSON'}
                  </p>
                  {!result.valid && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-red-300">{result.error}</p>
                      {result.position && (
                        <p className="text-sm text-red-400 font-mono">{result.position}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      </ToolLayout>
  );
}
